import { useHasPermission } from "./useHasPermission";
import { useMyPermissionsQuery } from "./usePermissionQueries";

export interface FinancialReadAccess {
  canReadBankAccounts: boolean;
  canReadMonthlyFees: boolean;
  canReadTransactions: boolean;
  /** True while terreiro permissions load; the `canRead*` flags are false until then. */
  isResolvingAccess: boolean;
}

/**
 * Mirrors the API read guards for the financial dashboard data. Personal
 * (medium-owned) finances are only scoped by ownership, so they are always readable.
 */
export const useFinancialReadAccess = (
  terreiroId?: string,
): FinancialReadAccess => {
  const code = (permission: string) => (terreiroId ? permission : undefined);

  const canManageAccounts = useHasPermission(
    terreiroId,
    code("financial.manage_accounts"),
  );
  const canViewOwnAccounts = useHasPermission(
    terreiroId,
    code("financial.manage_accounts_view.own"),
  );
  const canUpdateOwnAccounts = useHasPermission(
    terreiroId,
    code("financial.manage_accounts_update.own"),
  );
  const canViewTransactions = useHasPermission(
    terreiroId,
    code("financial.view_transactions"),
  );
  const canViewOwnTransactions = useHasPermission(
    terreiroId,
    code("financial.view_transactions.own"),
  );

  const { isLoading: isPermissionsLoading } = useMyPermissionsQuery({
    terreiroId,
    enabled: !!terreiroId,
  });

  if (!terreiroId) {
    return {
      canReadBankAccounts: true,
      canReadMonthlyFees: false,
      canReadTransactions: true,
      isResolvingAccess: false,
    };
  }

  return {
    canReadBankAccounts:
      canManageAccounts || canViewOwnAccounts || canUpdateOwnAccounts,
    canReadMonthlyFees: canViewTransactions,
    canReadTransactions: canViewTransactions || canViewOwnTransactions,
    isResolvingAccess: isPermissionsLoading,
  };
};
